import { Users, Zap } from 'lucide-react-native';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';

import { Text } from '@/components/Text';
import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import type { ProofIcon, ReferMarketing } from '@/services/referralStats';

/**
 * The two reassurance tiles under the story card: how many people this has
 * worked for, and how fast the money lands.
 *
 * They answer the two questions the story raises ("is that typical?" and
 * "when do I actually get paid?") before the program cards ask for a share,
 * which is the whole reason they sit between the two.
 *
 * Icons are Deep Green's accessible sibling (`colors.accentText`), not
 * Electric Green: DESIGN.md gives Electric Green one moment per screen and
 * that moment is a CTA, not decoration.
 */
const ICONS: Record<ProofIcon, typeof Users> = {
  people: Users,
  speed: Zap,
};

export interface ProofTilesProps {
  /**
   * The server's claims (`ReferMarketing.proof_points`), verbatim. Two are
   * expected — the row is 50/50 — but a third wraps to its own line rather
   * than squeezing the pair, and an icon name this build doesn't know
   * draws the text alone.
   */
  points: ReferMarketing['proof_points'];
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

export function ProofTiles({ points, style, testID }: ProofTilesProps) {
  if (points.length === 0) return null;
  return (
    <View style={[styles.row, style]} testID={testID}>
      {points.map((point, index) => {
        // Own-property check, not `in`: a server typo like "constructor"
        // would otherwise resolve through Object.prototype to a function
        // React can't render, and take the whole tab down with it.
        const Icon = Object.prototype.hasOwnProperty.call(ICONS, point.icon)
          ? ICONS[point.icon as ProofIcon]
          : null;
        return (
          // Index key: the list is short, server-ordered and never reordered
          // in place, and two claims may legitimately share their text.
          <View key={index} style={styles.tile}>
            {Icon ? <Icon size={20} color={colors.accentText} strokeWidth={2} /> : null}
            <Text style={styles.text}>{point.text}</Text>
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  tile: {
    flex: 1,
    // Two tiles split the row; a third (if the server ever sends one)
    // wraps rather than shrinking all three past legibility.
    minWidth: '45%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.base,
    // radii.base, not the cards' radii.lg: these are the smallest boxes on
    // the screen, and matching the 20 of a full-width card would make them
    // read as pills.
    borderRadius: radii.base,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
  text: {
    flex: 1,
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.sm,
    lineHeight: 18,
    color: colors.charcoal,
  },
});
